import { createTUID, splitMultipartUID, } from "@buildwithflux/models";
import { brandedKeys, normalizeRadians, roundVec2Float } from "@buildwithflux/shared";
import { kruskal } from "kruskal-mst";
import { compact, keyBy, mapValues, minBy, maxBy, pick } from "lodash";
import { box2, vec2 } from "../common/geometry";
import { makeSegmentId, splitSegmentId } from "../wiring/helpers";
import { Wiring } from "../wiring/Wiring";
import { ElementHelper } from "./ElementHelper";
import { SymbolLayout } from "./SymbolLayout";
// If we want to fine-tune this algorithm further, there are some good insights to be
// had from reading D3's explanation of their algorithm parameters. We might want to
// eg implement something like their `alpha` cooling parameter to help converge
// quicker and more accurately:
// https://d3js.org/d3-force/simulation#force-simulations
/** Given a set of elements to work with, adjust their positions into a more optimal arrangement,
 * using their current positions as a starting point. Internally uses a force-directed algorithm
 * that accounts for wire length, overlaps, non-orthogonal connections, misalignment, or other
 * factors.
 *
 * TODO I want to think through whether "rewards" might be better than "costs" for certain factors
 * that aren't continuous functions.
 * (the difference being that rewards are default-zero until within a reward criteria threshold.
 * I think it amounts to mathematically the same as assigning a fixed cost to those factors which
 * gets reduced for every one that's met, but it's a different way of thinking about it. I think
 * it might help get some strong "snapping-style" criteria right, like aligning precisely to things
 * that are nearby, without weighting the algo if the snap point is further than a threshold)
 *
 * TODO that brings up another point: some things are either on or off, and don't work well with
 * a force-directed paradigm. Eg, snapping has to be precise; it can't be partially-snapped but
 * nudged a bit out by other nearby forces. That's just not snapped. Force-directivity will get
 * it close (especially with eg inverse-square forces), but we might want to run a snapping pass
 * afterwards. If we apply a high force to snap, it'll encourage overshoot.
 *
 * TODO relatedly: a pin might have multiple discrete ideal locations, prioritized by the one
 * facing the other pin first, but secondarily by the position orthogonal to the pin, if it's
 * not intersecting the other component. We want to guide the pin to the second if the first
 * is non-ideal, but we don't want the first to exert a force on the pin if we've chosen the
 * second location. Maybe a sharply decaying force or cliff at a threshold? But we still want
 * to encourage the component to get in the region of the pin, so maybe a directionless spring
 * for the distance, then a directional force for the alignment? With a hill-shaped force curve
 * so it exerts strong force only when close.
 *
 * TODO then also maybe use the MST to determine which pins to wire, instead of rewiring the
 * same pairs that were originally wired. (Or maybe the original pairs matter, so with care.)
 *
 * TODO we should also add a repulsion force from other wires (excluding those connected to the
 * selected components). Or maybe from other wire *regions*, i.e. the bbox+margin of the terminals
 * being wired, so we can avoid wires that aren't yet placed.
 *
 * TODO try assigning each symbol a "mass" based on its bbox size or num pins, and downscale the
 * impact of forces on it accordingly. This will help big components act as anchors and I think
 * make the outcome more predictable and cleaner?
 *
 * TODO long-term, it might be better to include rotation as part of the force-directed loop,
 * including a torque force and converging to a better-facing angle each iteration (snapping
 * to the nearest cardinal direction at the end) rather than simply choosing the angle in a
 * single step.
 *
 * TODO for better untangling, we should try a first pass without the overlap forces, just the
 * spring forces, and then run a de-overlap pass (probably still including the spring forces?).
 * This would prevent obstacles from blocking a component from getting to its ideal position.
 */
// TODO merge DirectionVectors with the one in WireProjectionSmart.ts once I convert that to IVector2.
const DirectionVectors = {
    up: { x: 0, y: 1 },
    down: { x: 0, y: -1 },
    left: { x: -1, y: 0 },
    right: { x: 1, y: 0 },
};
/** Schematic auto-placement algorithm.
 *
 * Usage:
 *      positioner = new SchematicAutoPlacement(document, elementUidsToMove);
 *      positioner.tick();              // Run one iteration
 *      positioner.runLayout();         // Run until stopping force is reached
 *      positioner.rotateSymbols();     // Rotate symbols to face connected pins, before and/or after positioning
 *
 * Note: balancing the forces in this algorithm is a bit of an art, and we don't have the
 * ideal settings yet. Different situations respond better or worse to different balances,
 * so don't adjust a parameter without testing all scenarios.
 * I'm collecting scenarios in this project: http://localhost:3000/bryan/schematic-autoplacement-tests
 */
export class SchematicAutoPlacement {
    document;
    elementUidsToPlace;
    stoppingForce;
    elementBounds = {};
    elementPlacements = {};
    elementUidsWithWires = new Set();
    pinLocations = {};
    wireSegmentBounds = {};
    maxIterations;
    idealWireLength;
    numIterations = 0;
    initialTemperature;
    connectionsByTerminal = {};
    /** Vertices that are connected to the elements we're moving */
    connectedVertices = new Set();
    minimumSpanningEdges = {};
    updatedElementUids = [];
    constructor(document, elementUidsToPlace, opts = {}) {
        this.document = document;
        this.elementUidsToPlace = elementUidsToPlace;
        this.updateDataFromDocument(document);
        this.maxIterations = opts.maxIterations ?? 1000;
        this.stoppingForce = opts.stoppingForce ?? 0.5;
        this.idealWireLength = opts.idealWireLength ?? 40; // 2x pin length seems to work well.
        this.initialTemperature = opts.initialTemperature ?? 1;
    }
    updateDataFromDocument(document) {
        for (const element of Object.values(document.elements)) {
            this.updateDataFromElement(element);
        }
        this.updateDataFromWireSegments(document);
        this.updateDataFromConnections(document);
    }
    updateDataFromElement(element) {
        // TODO make this work with multisymbol elements!
        const symbolInfo = SymbolLayout.getSymbolInfo(element);
        const placement = Object.values(ElementHelper.getSymbolPlacements(element, symbolInfo))[0];
        this.elementPlacements[element.uid] = placement;
        this.elementBounds[element.uid] = ElementHelper.relativeBoundsToAbsolute(symbolInfo[0].outerBounds, {
            reference: placement,
        });
        this.pinLocations[element.uid] = mapValues(symbolInfo[0].pinLocations, (l) => ({
            position: ElementHelper.relativePosToAbsolute(l.position, { reference: placement }),
            direction: ElementHelper.relativeDirectionToAbsolute(l.direction, { reference: placement }),
        }));
    }
    updateDataFromWireSegments(document) {
        for (const vertex of Object.values(document.routeVertices)) {
            for (const otherId of brandedKeys(vertex.connectedVertices)) {
                const other = document.routeVertices[otherId];
                const segmentId = makeSegmentId([vertex.id, otherId]);
                if (segmentId in this.wireSegmentBounds)
                    continue;
                if (!other)
                    continue;
                this.wireSegmentBounds[segmentId] = box2.fromPoints([vertex.position, other.position]);
            }
        }
    }
    updateDataFromConnections(document) {
        const wiring = new Wiring(document.routeVertices, document.elements);
        // TODO vertices-by-terminal is yet another thing that SceneManagerEtc keeps track of
        // that's annoying+inefficient to recompute anywhere that doesn't depend on SceneManager.
        const verticesByTerminal = keyBy([...wiring.vertices.values()], (v) => v.terminal ? createTUID(v.terminal) : "");
        // Pre-calculate all the connected-by-wires terminals for each terminal in the elements we're moving
        for (const elementUid of this.elementUidsToPlace) {
            for (const terminalUid of Object.keys(this.pinLocations[elementUid] ?? {})) {
                const tuid = createTUID([elementUid, terminalUid]);
                if (this.connectionsByTerminal[tuid] || !verticesByTerminal[tuid])
                    continue;
                const vertex = verticesByTerminal[tuid];
                if (!vertex)
                    continue;
                const connectedVertices = wiring.getAllConnectedVertices(vertex.id);
                const terminals = compact(connectedVertices.map((uid) => wiring.vertices.get(uid)?.terminal));
                // eslint-disable-next-line @buildwithflux/no-array-foreach
                connectedVertices.forEach((uid) => this.connectedVertices.add(uid));
                const terminalSet = new Set(terminals);
                for (const terminal of terminals) {
                    this.connectionsByTerminal[createTUID(terminal)] = terminalSet;
                }
            }
        }
        this.elementUidsWithWires = new Set(Object.values(document.routeVertices)
            .map((v) => v.terminal?.element_uid)
            .filter((uid) => !!uid));
    }
    getPlacements() {
        return pick(this.elementPlacements, this.updatedElementUids);
    }
    /** Return the data with the updated positions etc for any elements that have been moved */
    getUpdatedElementData() {
        const updatedElements = {};
        const positions = this.getPlacements();
        for (const [elementUid, pos] of Object.entries(positions)) {
            const element = this.document.elements[elementUid];
            if (!element)
                continue;
            const symbolPlacements = ElementHelper.getSymbolPlacements(element);
            const oldPosition = Object.values(symbolPlacements)[0];
            // Include all original position fields (flip etc) as well as any new rotation
            // data etc from `pos`, and of course the new rounded position.
            const newPosition = { ...oldPosition, ...pos, ...roundVec2Float(pos, 1) };
            symbolPlacements[Object.keys(symbolPlacements)[0]] = newPosition;
            const newSubjectData = { ...element, symbol_placements: symbolPlacements };
            updatedElements[elementUid] = newSubjectData;
        }
        return updatedElements;
    }
    runLayout() {
        for (let i = 0; i < this.maxIterations; i++) {
            const maxForce = this.tick();
            if (vec2.len(maxForce) < this.stoppingForce)
                break;
        }
        return this.getPlacements();
    }
    tick() {
        let forces = {};
        // TODO arguably we only need to create the MST once at the start? It's may be unlikely to change
        // during iteration, since we're only moving closer to the ideal positions.
        this.createMinimumSpanningTrees();
        for (const elementUid of this.elementUidsToPlace) {
            forces[elementUid] = this.totalForceOnElement(elementUid).force;
        }
        // Add a small random perturbation to break out of situations when positions are identical
        // (common when components are initially placed by the same codepath, eg copilot), and other
        // unstable-equilibria. Do this before calculating the maxForce, and start above
        // .stoppingForce, to ensure the stop condition includes the perturbation initially, but
        // decrease with iterations so as not to override the stopping condition later on. (Note:
        // this does mean it will be doubly-decreased when the temperature is accounted for below.
        // I'm not sure of the cleanest way to avoid this, but it doesn't really affect the results)
        const randomVector = () => ({
            x: (this.stoppingForce + Math.random()) / (this.numIterations + 1),
            y: (this.stoppingForce + Math.random()) / (this.numIterations + 1),
        });
        forces = mapValues(forces, (f) => vec2.add(f, randomVector()));
        const maxForce = maxBy(Object.values(forces), (f) => vec2.len(f)) ?? { x: 0, y: 0 };
        // Decrease the "temperature" (amount of movement) as iterations increase, to help convergence:
        const displacements = mapValues(forces, (f) => vec2.scale(f, this.initialTemperature / Math.sqrt(this.numIterations + 1)));
        this.applyDisplacements(displacements);
        this.numIterations++;
        return maxForce;
    }
    resetIterator(opts = {}) {
        this.numIterations = 0;
        this.maxIterations = opts.maxIterations ?? this.maxIterations;
        this.stoppingForce = opts.stoppingForce ?? this.stoppingForce;
        this.idealWireLength = opts.idealWireLength ?? this.idealWireLength;
        this.initialTemperature = opts.initialTemperature ?? this.initialTemperature;
    }
    rotateSymbols() {
        const updatedElementData = this.getUpdatedElementData();
        let elementsRotated = 0;
        for (const [elementUid, element] of Object.entries(updatedElementData)) {
            const placement = Object.values(element.symbol_placements)[0];
            const origRotation = placement.orientation;
            let bestRotation = { angle: 0, tension: Infinity };
            for (let i = 0; i < 4; i++) {
                placement.orientation = normalizeRadians((placement.orientation ?? 0) + (i * Math.PI) / 2);
                this.updateDataFromElement(element);
                // TODO instead of straight-wire tension here, we could project a ProjectionSmart wire
                // between all terminals, and use the length+corners of that as the cost function, to give
                // a better measure of how clean a wire we'll get.
                const tension = this.totalForceOnElement(elementUid).tension;
                if (tension < bestRotation.tension) {
                    bestRotation = { angle: placement.orientation, tension };
                }
            }
            placement.orientation = bestRotation.angle;
            if (placement.orientation !== origRotation) {
                elementsRotated++;
            }
            this.updateDataFromElement(element);
        }
        return elementsRotated;
    }
    applyPlacements(placements) {
        const displacements = mapValues(placements, (placement, uid) => {
            const orig = this.elementPlacements[uid];
            if (!orig)
                throw new Error("Do not specify element uid that was not initialized in constructor");
            // Ensure to keep `...orig` below because the original may be a SchematicPosition2 and
            // contain more info than just x,y (for eg, orientation)
            return { ...orig, ...vec2.sub(placement, orig) };
        });
        return this.applyDisplacements(displacements);
    }
    applyDisplacements(displacements) {
        // Apply the displacements to the objects.
        for (const [elementUid, moveBy] of Object.entries(displacements)) {
            const bounds = this.elementBounds[elementUid];
            const orig = this.elementPlacements[elementUid];
            const pinLocations = this.pinLocations[elementUid];
            if (!(bounds && orig && pinLocations)) {
                throw new Error("Do not specify element uid that was not initialized in constructor");
            }
            // Ensure to keep `...orig` below because the original may be a SchematicPosition2 and
            // contain more info than just x,y (for eg, orientation)
            this.elementPlacements[elementUid] = { ...orig, ...vec2.add(orig, moveBy) };
            this.elementBounds[elementUid] = box2.translate(bounds, moveBy);
            this.pinLocations[elementUid] = mapValues(pinLocations, (p) => ({
                ...p,
                position: vec2.add(p.position, moveBy),
            }));
            this.updatedElementUids.push(elementUid);
        }
    }
    totalForceOnElement(elementUid) {
        const forces = [
            this.forceFromElements(elementUid),
            this.forceFromWireSegments(elementUid),
            this.forceFromConnections(elementUid),
        ];
        return {
            force: forces.reduce((acc, f) => vec2.add(acc, f.force), { x: 0, y: 0 }),
            tension: forces.reduce((acc, f) => acc + f.tension, 0),
        };
    }
    /** Encourage elements to avoid overlap */
    forceFromElements(elementUid) {
        let force = { x: 0, y: 0 };
        let tension = 0;
        const margin = 15;
        const thisBounds = this.elementBounds[elementUid];
        if (!thisBounds) {
            throw new Error("Non-existent `elementUid` provided to forceFromElements");
        }
        const thisCenter = box2.center(thisBounds);
        // TODO we should be able to make this not N^2 this by using an rbush and only checking
        // elements that are within the expanded bounds -- making far-away elements not affect each
        // other. Worth it!
        for (const [otherUid, otherBounds] of Object.entries(this.elementBounds)) {
            if (otherUid === elementUid)
                continue;
            const otherCenter = box2.center(otherBounds);
            const expandedBounds = {
                this: box2.expandByScalar(thisBounds, margin),
                other: box2.expandByScalar(otherBounds, margin),
            };
            const overlap = box2.intersect(expandedBounds.this, expandedBounds.other);
            const fromOtherCenter = vec2.sub(thisCenter, otherCenter);
            let partialForce;
            if (overlap) {
                // Strong aversion to overlapping. Use a factor of > 1x below because we want
                // overlap forces to easily outweigh wire forces (overlaps are worse than
                // longer/shorter wires)
                const displacements = this.overlapDisplacements(expandedBounds.this, expandedBounds.other);
                // Repel from centers, not just in the axis-aligned direction of min overlap, to
                // encourage movement around each other.
                const overlapMagnitude = Math.min(...displacements.map((d) => vec2.len(d)));
                partialForce = vec2.scaleTo(fromOtherCenter, 1.5 * overlapMagnitude) ?? { x: 1, y: 0 };
            }
            else {
                // Minor aversion to being too close if not overlapping, inverse square.
                // TODO arguably this case is ~irrelevant for our use cases, because the margin
                // above is closer to what we care about. Maybe this force should be zero?
                // Or maybe it would be better (less jumping) to not have discontinuities, and
                // ensure we slope the force to ~zero from the value at the edge of the margin.
                const distance = Math.max(0.1, vec2.len(fromOtherCenter));
                const inverseSquare = 1 / distance ** 2;
                partialForce = vec2.scaleTo(fromOtherCenter, inverseSquare) ?? { x: 0, y: 0 };
            }
            force = vec2.add(force, partialForce);
            tension += vec2.len(partialForce);
        }
        return { force, tension };
    }
    /** Encourage elements to not overlap wires. Very similar logic to `forceFromElements`
     * but with different rules.
     */
    forceFromWireSegments(elementUid) {
        let force = { x: 0, y: 0 };
        let tension = 0;
        const margin = 15;
        const thisBounds = this.elementBounds[elementUid];
        if (!thisBounds) {
            throw new Error("Non-existent `elementUid` provided to forceFromWireSegments");
        }
        for (const [segmentId, segmentBounds] of Object.entries(this.wireSegmentBounds)) {
            const vertexIds = splitSegmentId(segmentId);
            if (!vertexIds)
                continue;
            // Don't try and avoid wires connected to the elements we're moving:
            if (vertexIds.some((id) => this.connectedVertices.has(id)))
                continue;
            const expandedBounds = {
                this: box2.expandByScalar(thisBounds, margin),
                other: box2.expandByScalar(segmentBounds, margin),
            };
            const overlap = box2.intersect(expandedBounds.this, expandedBounds.other);
            let partialForce;
            if (overlap) {
                const displacements = this.overlapDisplacements(expandedBounds.this, expandedBounds.other);
                const displacement = minBy(displacements, (d) => vec2.len(d));
                partialForce = vec2.scale(displacement, 1.5);
            }
            else {
                // No aversion to being close if outside of margin.
                continue;
            }
            force = vec2.add(force, partialForce);
            tension += vec2.len(partialForce);
        }
        return { force, tension };
    }
    /** Return the amount in each direction needed to move `box` to de-overlap it from `other` */
    overlapDisplacements(box, other) {
        return [
            { x: other.max.x - box.min.x, y: 0 },
            { x: other.min.x - box.max.x, y: 0 },
            { x: 0, y: other.max.y - box.min.y },
            { x: 0, y: other.min.y - box.max.y },
        ];
    }
    createMinimumSpanningTrees() {
        const terminalsProcessed = new Set();
        this.minimumSpanningEdges = {};
        for (const [terminalUid, pins] of Object.entries(this.connectionsByTerminal)) {
            if (terminalsProcessed.has(terminalUid))
                continue;
            const denseGraph = [];
            for (const pin1 of pins) {
                for (const pin2 of pins) {
                    const pin1Uid = createTUID(pin1);
                    const pin2Uid = createTUID(pin2);
                    const pin1Location = this.pinLocations[pin1.element_uid]?.[pin1.terminal_uid];
                    const pin2Location = this.pinLocations[pin2.element_uid]?.[pin2.terminal_uid];
                    if (pin1Uid === pin2Uid)
                        continue;
                    if (!pin1Location || !pin2Location)
                        continue;
                    const weight = vec2.len(this.forceBetweenPinsWhenConnected(pin1, pin2));
                    denseGraph.push({ from: pin1Uid, to: pin2Uid, weight });
                }
            }
            const mst = kruskal(denseGraph);
            for (const edge of mst) {
                (this.minimumSpanningEdges[edge.from] ??= []).push(edge.to);
                (this.minimumSpanningEdges[edge.to] ??= []).push(edge.from);
            }
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            pins.forEach((t) => terminalsProcessed.add(createTUID(t)));
        }
    }
    forceBetweenPinsWhenConnected(pin, other) {
        const thisLocation = this.pinLocations[pin.element_uid]?.[pin.terminal_uid];
        const otherLocation = this.pinLocations[other.element_uid]?.[other.terminal_uid];
        if (!otherLocation || !thisLocation) {
            return { x: 0, y: 0 };
        }
        // Attract an extension from this pin towards an extension from the other pin.
        // Ensure the point-of-attraction is at an equal extension from each pin, ensures that
        // a) the joining wires are straight if pins are facing
        // b) the wires are perpendicular at the same distance if the pins perpendicular
        // c) the force is symmetric and we solve for the same equilibrium regardless
        //    of which of the two pins we're calculating for.
        let otherExtension, thisExtension;
        if (otherLocation.direction === thisLocation.direction) {
            const axis = ["up", "down"].includes(thisLocation.direction) ? "x" : "y";
            const sign = otherLocation.position[axis] - thisLocation.position[axis] > 0 ? 1 : -1;
            const axisVector = axis === "x" ? { x: 1, y: 0 } : { x: 0, y: 1 };
            thisExtension = vec2.scale(axisVector, (sign * this.idealWireLength) / 2);
            otherExtension = vec2.scale(axisVector, (-sign * this.idealWireLength) / 2);
        }
        else {
            thisExtension = vec2.scale(DirectionVectors[thisLocation.direction], this.idealWireLength / 2);
            otherExtension = vec2.scale(DirectionVectors[otherLocation.direction], this.idealWireLength / 2);
        }
        const partialForce = vec2.sub(vec2.add(otherLocation.position, otherExtension), vec2.add(thisLocation.position, thisExtension));
        return partialForce;
    }
    /** Treat each wire a bit like a spring, encouraging wires to be straight and an ideal length */
    forceFromConnections(elementUid) {
        let force = { x: 0, y: 0 };
        let tension = 0;
        for (const pinUid of Object.keys(this.pinLocations[elementUid] ?? {})) {
            const terminalUid = createTUID([elementUid, pinUid]);
            // Only consider nearby pins (according to a minimum-spanning-tree), so we don't attempt
            // to pull the element to a useless average location.
            const nearby = this.minimumSpanningEdges[terminalUid] ?? [];
            for (const otherUid of nearby) {
                const [otherElementUid, otherTerminalUid] = splitMultipartUID(otherUid);
                const partialForce = this.forceBetweenPinsWhenConnected({ element_uid: elementUid, terminal_uid: pinUid }, { element_uid: otherElementUid, terminal_uid: otherTerminalUid });
                const magnitude = vec2.len(partialForce);
                force = vec2.add(force, partialForce);
                tension += magnitude;
            }
        }
        return { force, tension };
    }
}
//# sourceMappingURL=SchematicAutoPlacement.js.map